/**
 * useRequestBoard — the Request Board's data layer, against the real
 * `/request_board` API (src/api/requestBoardApi.js).
 *
 * Holds the cards and the board's people, and exposes one function per user
 * action. Every mutation returns the server's updated card, which replaces the
 * local copy; moves and task ticks are applied optimistically first so dragging
 * stays instant, and a failure puts the board back by refetching it.
 *
 * Live updates: the backend pushes `request_board_updated` (with the changed
 * card and its history events) to everyone on the board except the actor;
 * RealtimeProvider hands those to `subscribeBoard`. While realtime isn't
 * connected the board polls `GET /items` every 30s instead.
 *
 * `changeTick` / `lastChange` bump on every change (yours or someone else's) so
 * the screen can refetch an open card's history or the History tab.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";

import { requestBoardApi as api, requestBoardErrorMessage } from "../../api/requestBoardApi";
import { useRealtime } from "../../realtime/RealtimeProvider";
import {
  COL,
  cleanNeeds,
  mentionsIn,
  rankBetween,
  registerPeople,
} from "./requestBoardModel";

const POLL_MS = 30_000;

/** Cards come back keyed by `_id`; the board keys everything by `id`. */
export const normalizeItem = (it) =>
  it
    ? {
        ...it,
        id: String(it._id ?? it.id),
        needs: cleanNeeds(it.needs),
        urgency: it.urgency || "normal",
        answer: it.answer ?? null,
        comments: it.comments || [],
        images: it.images || [],
        hitCount: Number(it.hitCount) || 0,
        hitsByPerson: Array.isArray(it.hitsByPerson) ? it.hitsByPerson : [],
      }
    : it;

export const normalizeEvent = (e) => ({ ...e, id: String(e._id ?? e.id) });

const sortByRank = (list) => [...list].sort((a, b) => a.rank - b.rank);

/** Ignore a second encounter on the same card within this window (double clicks). */
const HIT_COOLDOWN_MS = 3000;

/** The card with one more count from `me`, for the optimistic update. */
const withHit = (item, me) => {
  const at = new Date().toISOString();
  const people = [...(item.hitsByPerson || [])];
  const i = people.findIndex((p) => p.by === me);
  if (i >= 0) people[i] = { ...people[i], count: people[i].count + 1, lastAt: at };
  else people.push({ by: me, count: 1, lastAt: at });
  return { ...item, hitCount: (item.hitCount || 0) + 1, hitsByPerson: people, lastHitAt: at, lastHitBy: me };
};

/**
 * Many cards to the bottom of `to`, in their current order — the local stand-in
 * for `POST /items/move_many` (cards already there are left alone).
 */
const optimisticMoveMany = (items, ids, to) => {
  const moving = new Set(ids);
  const lane = sortByRank(items.filter((i) => i.column === to && !moving.has(i.id)));
  let last = lane[lane.length - 1]?.rank;
  const ranks = new Map();
  sortByRank(items.filter((i) => moving.has(i.id) && i.column !== to)).forEach((i) => {
    last = rankBetween(last, undefined);
    ranks.set(i.id, last);
  });
  return items.map((i) => (ranks.has(i.id) ? { ...i, column: to, rank: ranks.get(i.id) } : i));
};

/**
 * Local stand-in for the server's rank math, for the optimistic move. In
 * Requests the neighbours are the cards in the same section (Urgent or Queue),
 * so the target urgency decides the lane. A card coming back from Requires
 * Backend Changes lands at the top of Frontend In Progress, as the server does.
 */
const optimisticMove = (items, id, to, beforeId, urgency) => {
  const item = items.find((i) => i.id === id);
  if (!item) return items;
  const nextUrgency = urgency || item.urgency || "normal";
  const sameSection = (i) =>
    to !== COL.REQUESTS || (i.urgency === "urgent") === (nextUrgency === "urgent");
  const lane = sortByRank(items.filter((i) => i.column === to && i.id !== id && sameSection(i)));
  const target =
    item.column === COL.BACKEND && to === COL.PROGRESS ? lane[0]?.id ?? null : beforeId;
  const at = target == null ? lane.length : lane.findIndex((i) => i.id === target);
  const idx = at < 0 ? lane.length : at;
  const rank = rankBetween(lane[idx - 1]?.rank, lane[idx]?.rank);
  return items.map((i) => (i.id === id ? { ...i, column: to, rank, urgency: nextUrgency } : i));
};

/**
 * Upload whatever in `list` is new (has a `file`) and return every entry's
 * attachment id, in order. Entries already on the server keep their `id`.
 */
const attachmentIds = async (list = []) => {
  const fresh = list.filter((x) => x?.file);
  const uploaded = fresh.length ? await api.upload(fresh.map((x) => x.file)) : [];
  let k = 0;
  return list
    .map((x) => (x?.file ? uploaded[k++]?.id : x?.id))
    .filter(Boolean);
};

/** The backend-details body from the dialog's blocker (new files uploaded first). */
const blockerBody = async (blocker) => ({
  type: blocker.type,
  text: blocker.text,
  spec: blocker.spec || "",
  specFile: blocker.specFile || null,
  attachmentIds: await attachmentIds(blocker.files || []),
  tasks: (blocker.items || []).map((x) => ({ t: x.t, done: !!x.done })),
});

export default function useRequestBoard() {
  const { enqueueSnackbar } = useSnackbar();
  const { subscribeBoard, status: realtimeStatus } = useRealtime();

  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Bumped on every change; `lastChange` says which card and carries any events.
  const [changeTick, setChangeTick] = useState(0);
  const lastChangeRef = useRef(null);

  const itemsRef = useRef(items);
  itemsRef.current = items;

  const noteChange = useCallback((itemId, events = []) => {
    lastChangeRef.current = { itemId, events: events.map(normalizeEvent) };
    setChangeTick((t) => t + 1);
  }, []);

  const upsert = useCallback((raw) => {
    const item = normalizeItem(raw);
    if (!item) return null;
    setItems((prev) =>
      prev.some((i) => i.id === item.id) ? prev.map((i) => (i.id === item.id ? item : i)) : [...prev, item],
    );
    return item;
  }, []);

  const refreshItems = useCallback(async () => {
    const res = await api.getItems();
    setItems((res.items || []).map(normalizeItem));
    setTotal(res.counts?.total ?? (res.items || []).length);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [meta, board] = await Promise.all([api.getMeta(), api.getItems()]);
      registerPeople([...(meta.people || []), meta.me].filter(Boolean));
      setMe(meta.me?.email?.toLowerCase() || null);
      setItems((board.items || []).map(normalizeItem));
      setTotal(board.counts?.total ?? (board.items || []).length);
    } catch (err) {
      setLoadError(requestBoardErrorMessage(err, "The board couldn't be loaded."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Show the error and put the board back the way the server has it. */
  const fail = useCallback(
    (err, fallback) => {
      enqueueSnackbar(requestBoardErrorMessage(err, fallback), { variant: "error" });
      refreshItems().catch(() => {});
    },
    [enqueueSnackbar, refreshItems],
  );

  /** Run a mutation that returns the updated card; upsert it and note the change. */
  const run = useCallback(
    async (call, fallback) => {
      try {
        const item = upsert(await call());
        if (item) noteChange(item.id);
        return item;
      } catch (err) {
        fail(err, fallback);
        return null;
      }
    },
    [upsert, noteChange, fail],
  );

  /* ── live updates ─────────────────────────────────────────────────────── */

  useEffect(
    () =>
      subscribeBoard((ev) => {
        // A bulk move (Complete All / Approve All) arrives as one push with every card.
        if (Array.isArray(ev.items)) {
          ev.items.forEach(upsert);
          noteChange(null, ev.events || []);
          return;
        }
        if (ev.item) {
          const known = itemsRef.current.some((i) => i.id === String(ev.item._id ?? ev.item.id));
          upsert(ev.item);
          if (!known) setTotal((t) => t + 1);
        } else if (ev.itemId) {
          setItems((prev) => prev.filter((i) => i.id !== String(ev.itemId)));
          setTotal((t) => Math.max(0, t - 1));
        }
        noteChange(String(ev.itemId || ev.item?._id || ""), ev.events || []);
      }),
    [subscribeBoard, upsert, noteChange],
  );

  // Polling fallback while the live socket isn't connected.
  useEffect(() => {
    if (realtimeStatus === "connected") return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") refreshItems().catch(() => {});
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [realtimeStatus, refreshItems]);

  /* ── cards ────────────────────────────────────────────────────────────── */

  const createItem = useCallback(
    async ({ images = [], ...fields }, position) => {
      try {
        const ids = await attachmentIds(images);
        const item = upsert(
          await api.createItem({
            ...fields,
            needs: cleanNeeds(fields.needs),
            urgency: fields.urgency || "normal",
            position,
            attachmentIds: ids,
          }),
        );
        if (item) {
          setTotal((t) => t + 1);
          noteChange(item.id);
        }
        return item;
      } catch (err) {
        fail(err, "The request couldn't be added.");
        return null;
      }
    },
    [upsert, noteChange, fail],
  );

  const updateItem = useCallback(
    (id, patch) => run(() => api.updateItem(id, patch), "The change couldn't be saved."),
    [run],
  );

  const deleteItem = useCallback(
    async (id) => {
      try {
        await api.deleteItem(id);
        setItems((prev) => prev.filter((i) => i.id !== id));
        setTotal((t) => Math.max(0, t - 1));
        noteChange(id);
        return true;
      } catch (err) {
        fail(err, "The request couldn't be deleted.");
        return false;
      }
    },
    [noteChange, fail],
  );

  /** Any column / position change except into Requires Backend Changes. */
  const moveItem = useCallback(
    (id, to, beforeId = null, comment = null, { urgency, needs } = {}) => {
      setItems((prev) => optimisticMove(prev, id, to, beforeId, urgency));
      return run(async () => {
        let body = { to, beforeId };
        if (urgency) body.urgency = urgency;
        if (needs) body.needs = cleanNeeds(needs);
        if (comment) {
          const ids = await attachmentIds(comment.images);
          body = {
            ...body,
            comment: { body: comment.body, attachmentIds: ids, mentions: mentionsIn(comment.body) },
          };
        }
        return api.moveItem(id, body);
      }, "The card couldn't be moved.");
    },
    [run],
  );

  /**
   * Move several cards to one stage in one request — Complete All / Approve All
   * (`POST /items/move_many`, update request 2026-10-20). Only cards still in
   * `from` move; the server says which it skipped. Returns the moved count, or
   * null on failure (the board is put back).
   */
  const moveMany = useCallback(
    async (ids, to, from) => {
      if (!ids?.length) return 0;
      setItems((prev) => optimisticMoveMany(prev, ids, to));
      try {
        const res = await api.moveMany({ itemIds: ids, to, from });
        (res.moved || []).forEach(upsert);
        if (res.skipped?.length) refreshItems().catch(() => {});
        noteChange(null);
        return (res.moved || []).length;
      } catch (err) {
        fail(err, "The cards couldn't be moved.");
        return null;
      }
    },
    [upsert, noteChange, fail, refreshItems],
  );

  /* ── Recurring Encounters ─────────────────────────────────────────────── */

  const lastHitRef = useRef({});

  /**
   * Log one more encounter on a card. Counted on screen straight away; the
   * server's card replaces it. Returns { item, hit } (hit.id is what Undo
   * removes), or null when ignored (a double click) or it failed.
   */
  const addHit = useCallback(
    async (id) => {
      const now = Date.now();
      if (now - (lastHitRef.current[id] || 0) < HIT_COOLDOWN_MS) return null;
      lastHitRef.current[id] = now;
      setItems((prev) => prev.map((i) => (i.id === id ? withHit(i, me) : i)));
      try {
        const res = await api.addHit(id);
        const item = upsert(res?.item ?? res);
        if (item) noteChange(item.id);
        return { item, hit: res?.hit || null };
      } catch (err) {
        lastHitRef.current[id] = 0;
        fail(err, "The encounter couldn't be logged.");
        return null;
      }
    },
    [me, upsert, noteChange, fail],
  );

  const removeHit = useCallback(
    (id, hitId) => run(() => api.removeHit(id, hitId), "That count couldn't be removed."),
    [run],
  );

  /* ── backend details ──────────────────────────────────────────────────── */


  const addBackendDetails = useCallback(
    (id, beforeId, blocker) => {
      setItems((prev) => optimisticMove(prev, id, COL.BACKEND, beforeId));
      return run(
        async () => api.addBackendDetails(id, { beforeId, ...(await blockerBody(blocker)) }),
        "The card couldn't be moved to Requires Backend Changes.",
      );
    },
    [run],
  );

  const editBackendDetails = useCallback(
    (id, blocker) =>
      run(async () => api.editBackendDetails(id, await blockerBody(blocker)), "The backend details couldn't be saved."),
    [run],
  );

  const setTask = useCallback(
    (id, taskId, done) => {
      setItems((prev) =>
        prev.map((i) =>
          i.id === id && i.blocker
            ? {
                ...i,
                blocker: {
                  ...i.blocker,
                  items: i.blocker.items.map((x) => (x.id === taskId ? { ...x, done } : x)),
                },
              }
            : i,
        ),
      );
      return run(() => api.setTask(id, taskId, done), "The task couldn't be updated.");
    },
    [run],
  );

  /* ── comments and images ──────────────────────────────────────────────── */

  const addComment = useCallback(
    (id, body, images = []) =>
      run(
        async () =>
          api.addComment(id, {
            body,
            attachmentIds: await attachmentIds(images),
            mentions: mentionsIn(body),
          }),
        "The comment couldn't be posted.",
      ),
    [run],
  );

  const addImages = useCallback(
    (id, images) =>
      run(async () => api.addImages(id, await attachmentIds(images)), "The images couldn't be added."),
    [run],
  );

  const removeImage = useCallback(
    (id, imageId) => run(() => api.removeImage(id, imageId), "The image couldn't be removed."),
    [run],
  );

  /** Answer a question: saves the answer and moves the card to review. */
  const answerItem = useCallback(
    (id, answer) => {
      setItems((prev) => optimisticMove(prev, id, COL.REVIEW, sortByRank(prev.filter((i) => i.column === COL.REVIEW && i.id !== id))[0]?.id ?? null));
      return run(() => api.answerItem(id, answer), "The answer couldn't be saved.");
    },
    [run],
  );

  /** A card that isn't on the board (e.g. an older Completed one) — for `?item=`. */
  const fetchItem = useCallback(
    async (idOrNumber) => {
      try {
        return upsert(await api.getItem(idOrNumber));
      } catch {
        return null;
      }
    },
    [upsert],
  );

  /**
   * The Refresh button: refetch the board (and people) in place, without the
   * full-page spinner, then bump changeTick so an open card's history and the
   * History tab refetch too.
   */
  const [refreshing, setRefreshing] = useState(false);
  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const [meta, board] = await Promise.all([api.getMeta(), api.getItems()]);
      registerPeople([...(meta.people || []), meta.me].filter(Boolean));
      setItems((board.items || []).map(normalizeItem));
      setTotal(board.counts?.total ?? (board.items || []).length);
      noteChange(null);
    } catch (err) {
      enqueueSnackbar(requestBoardErrorMessage(err, "The board couldn't be refreshed."), {
        variant: "error",
      });
    } finally {
      setRefreshing(false);
    }
  }, [noteChange, enqueueSnackbar]);

  return {
    me,
    refresh,
    refreshing,
    items,
    total,
    loading,
    loadError,
    reload: load,
    changeTick,
    lastChangeRef,
    createItem,
    updateItem,
    deleteItem,
    moveItem,
    moveMany,
    addHit,
    removeHit,
    answerItem,
    addBackendDetails,
    editBackendDetails,
    setTask,
    addComment,
    addImages,
    removeImage,
    fetchItem,
  };
}
